import { PlanType } from "@brookbush/database";
import type { PlanConfig } from "@brookbush/types";

type RecurringInterval = "day" | "week" | "month" | "year";

export type MembershipPriceLike = {
	id: string;
	recurring?: { interval: RecurringInterval } | null;
};

/**
 * Pick the default membership price for checkout.
 * New buyers get annual. Existing members get the other interval than their current plan.
 */
export const resolveDefaultMembershipPrice = <T extends MembershipPriceLike>(
	prices: T[],
	currentInterval?: string | null,
): T | undefined => {
	if (!prices.length) return undefined;

	if (currentInterval) {
		return (
			prices.find((price) => price.recurring?.interval !== currentInterval) ??
			prices[0]
		);
	}

	return (
		prices.find((price) => price.recurring?.interval === "year") ?? prices[0]
	);
};

/**
 * Keep the selected price only while that id is still in the offered list.
 * A missing list means prices are still loading, so the selection stays.
 *
 * @param {readonly { id: string }[] | undefined} prices - Prices on offer
 * @param {string | undefined} priceId - Currently selected Stripe price id
 * @returns {string | undefined} The same id, or undefined when it is no longer offered
 */
export const reconcileMembershipPriceId = (
	prices: readonly { id: string }[] | undefined,
	priceId: string | undefined,
): string | undefined => {
	if (prices == null || !priceId) return priceId;

	return prices.some((price) => price.id === priceId) ? priceId : undefined;
};

/**
 * Whether two plan configs would write the same checkout selection.
 * Used to skip a store update that would otherwise re-render forever.
 *
 * @param {PlanConfig} current - Config already in the store
 * @param {PlanConfig} next - Config an effect is about to write
 * @returns {boolean} True when every stored field matches
 */
export const planConfigsEqual = (
	current: PlanConfig,
	next: PlanConfig,
): boolean => {
	if (current.planType !== next.planType) return false;
	if (current.priceId !== next.priceId) return false;
	if (current.promoCodeId !== next.promoCodeId) return false;
	if (current.currency !== next.currency) return false;
	if (current.trialLength !== next.trialLength) return false;
	if (current.planType === PlanType.TEAM && next.planType === PlanType.TEAM) {
		return current.quantity === next.quantity;
	}

	return true;
};

/**
 * The membership modal only sells an individual plan, and only while it is open.
 * Resetting while it is closed fights the team checkout page and loops.
 *
 * @param {boolean} isOpen - Whether the become-a-member modal is open
 * @param {PlanType} planType - Plan type currently in the store
 * @returns {boolean} True when the modal should switch the store to individual
 */
export const shouldResetModalPlanToIndividual = (
	isOpen: boolean,
	planType: PlanType,
): boolean => isOpen && planType === PlanType.TEAM;

/** Dollars saved by paying annually instead of monthly × 12. Never negative. */
export const getAnnualSavingsVsMonthly = (
	monthlyAmount: number,
	yearlyAmount: number,
): number => Math.max(0, Math.floor(monthlyAmount * 12 - yearlyAmount));

/** Yearly total expressed as a monthly equivalent (2 decimal places). */
export const getAnnualMonthlyEquivalent = (yearlyAmount: number): number =>
	+(yearlyAmount / 12).toFixed(2);
